import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWebsitePublishCapabilityFeature } from '../../hooks/useWebsitePublishCapabilityFeature';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';
import { useToast, Flex, Box } from '@withjoy/joykit';
import { noop } from 'lodash-es';
import { useEventInfo } from '@shared/utils/eventInfo';
import { pxToRem } from '@withjoy/joykit/theme';
import { getAbsoluteUrlFromPathname } from '@shared/utils/urlHelpers';

interface UseUnpublishedWebsiteWarningParams {
  context: string | { category: string; page?: string };
  extraInfo?: Record<string, unknown>;
}

export const useUnpublishedWebsiteWarning = (params: UseUnpublishedWebsiteWarningParams) => {
  const { context, extraInfo = {} } = params;
  const { eventInfo, eventHandle } = useEventInfo();
  const eventId = eventInfo?.eventId || '';
  const { tq } = useTranslation('websiteDesigner');
  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const promiseResolveRef = useRef(noop);
  const { toast } = useToast();
  const analytics = useContext(AnalyticsContext);

  const [isWarningOpen, setIsWarningOpen] = useState(false);

  useEffect(() => {
    return () => {
      promiseResolveRef.current();
      promiseResolveRef.current = noop;
    };
  }, []);

  /**
   * Opens the unpublished website warning modal if the current event
   * is eligible for website exposure but hasn't been published yet.
   *
   * Returns a Promise that resolves when the user takes action
   * (either to publish or keep hidden). This allows calling code to
   * await the user's choice before proceeding (e.g., pausing a share or send flow).
   */
  const openUnpublishedWebsiteWarning = useCallback(() => {
    const isWebsiteExposed = websitePublishCapabilityFeature.eventExposure.value;
    const isWebsitePublished = websitePublishCapabilityFeature.eventPublishedState.value;

    // If feature is enabled and the website isn't published, prompt the user.
    if (isWebsiteExposed && !isWebsitePublished) {
      // Return a promise that will resolve when the user closes/interacts with the warning.
      const promise = new Promise(resolve => {
        promiseResolveRef.current = resolve;
      });

      setIsWarningOpen(true);

      return promise;
    }

    // Otherwise, resolve immediately (no warning needs to be shown).
    return Promise.resolve();
  }, [websitePublishCapabilityFeature.eventExposure.value, websitePublishCapabilityFeature.eventPublishedState.value]);

  const handlePublishClick = useCallback(async () => {
    try {
      await websitePublishCapabilityFeature.eventPublishedState.update(true);
      toast(
        <Flex
          alignItems="center"
          gap={5}
        >
          {tq`🥳 Your site has been published.`}
          <Box
            as="a"
            href={getAbsoluteUrlFromPathname(`/${eventHandle}`)}
            target="_blank"
            rel="noopener noreferrer"
            typographyVariant="label4"
            textDecoration="underline"
            textDecorationStyle="dotted"
            fontWeight={500}
            style={{
              textUnderlineOffset: pxToRem(2),
            }}
          >
            {tq`View Your Site →`}
          </Box>
        </Flex>,
      );

      analytics.track({
        category: typeof context === 'string' ? context : context.category,
        action: 'PublishGoLive',
        extraInfo: {
          eventId,
          ...extraInfo,
        },
      });

      promiseResolveRef.current();
      promiseResolveRef.current = noop;
      setIsWarningOpen(false);
    } catch (error) {
      addError(error, { context: 'useUnpublishedWebsiteWarning#handlePublishClick' });
      toast(tq`😬 Failed to publish website. Please try again.`);
    }
  }, [websitePublishCapabilityFeature.eventPublishedState, toast, tq, eventHandle, analytics, context, eventId, extraInfo]);

  const handleKeepHiddenClick = useCallback(() => {
    promiseResolveRef.current();
    promiseResolveRef.current = noop;
    setIsWarningOpen(false);
  }, []);

  const warningProps = useMemo(
    () => ({
      isOpen: isWarningOpen,
      onPublish: handlePublishClick,
      isPublishing: websitePublishCapabilityFeature.eventPublishedState.isUpdating,
      onKeepHidden: handleKeepHiddenClick,
    }),
    [isWarningOpen, handlePublishClick, handleKeepHiddenClick, websitePublishCapabilityFeature.eventPublishedState.isUpdating],
  );

  return {
    openUnpublishedWebsiteWarning,
    warningProps,
  };
};
